import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MDEditor from "@uiw/react-md-editor";
import {
  Button,
  TextField,
  Paper,
  Card,
  CardContent,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ChecklistIcon from "@mui/icons-material/Checklist";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import ConfettiExplosion from "react-confetti-explosion";
import { v4 as uuidv4 } from "uuid";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import useLocalStorage from "use-local-storage";

import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";

import Switch from "@mui/material/Switch";

import { useAtom } from "jotai";
import {
  isMobileBrowserAtom,
  themeAtom,
  currentStaffDataAtom,
  filterWhenCompletingAtom,
  groupWhenFilteringAtom,
} from "../../../../-Atoms";

import { db, storage } from "../../../../firebaseConfig";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import PopupState, { bindHover, bindPopover } from "material-ui-popup-state";
import HoverPopover from "material-ui-popup-state/HoverPopover";

const ChecklistsV2 = ({
  jobData,
  clientData,
  staffData,
  findStaff,
  setUrlModalUrl,
  setURLModalOpen,
  idList,
}) => {
  const [theme] = useAtom(themeAtom);
  const [checklists, setChecklists] = useState([]);

  useEffect(() => {
    if (jobData?.checklists) {
      setChecklists(jobData.checklists);
    }
  }, [jobData]);

  // Auto-cleanup empty checklists when job becomes Post Production
  useEffect(() => {
    if (jobData?.studio === "Post Production" && jobData?.checklists && jobData.checklists.length > 0) {
      // Check if all existing checklists are empty (no items)
      const allEmpty = jobData.checklists.every(checklist => 
        !checklist.checklistItems || checklist.checklistItems.length === 0
      );
      
      // If all checklists are empty and we have more than one, or none are named "Checklist"
      const hasChecklistNamed = jobData.checklists.some(checklist => checklist.name === "Checklist");
      
      if (allEmpty && (!hasChecklistNamed || jobData.checklists.length > 1)) {
        // Replace with single "Checklist"
        const newChecklist = [
          {
            name: "Checklist",
            id: uuidv4(),
            checklistItems: [],
          }
        ];
        
        updateDoc(doc(db, "JobData", jobData.id), {
          checklists: newChecklist,
        }).catch((e) => console.log(e));
      }
    }
  }, [jobData?.studio, jobData?.checklists, jobData?.id]);

  const onDragEnd = (e) => {
    if (!e.destination) return;

    let sourceChecklist;
    let destinationChecklist;
    checklists.forEach((cl) => {
      if (cl.id === e.source.droppableId) {
        sourceChecklist = cl;
      }
      if (cl.id === e.destination.droppableId) {
        destinationChecklist = cl;
      }
    });

    const sameList = e.source.droppableId === e.destination.droppableId;

    if (sameList) {
      const items = reorder(
        sameList
          ? sourceChecklist.checklistItems
          : destinationChecklist.checklistItems,
        e.source.index,
        e.destination.index
      );
      sourceChecklist.checklistItems = items;
    } else {
      const result = move(
        sourceChecklist.checklistItems,
        destinationChecklist.checklistItems,
        e.source,
        e.destination
      );
      destinationChecklist.checklistItems = result.destination;
      sourceChecklist.checklistItems = result.source;
    }
    setChecklists([...checklists]);
    updateDoc(doc(db, "JobData", jobData.id), {
      checklists: checklists,
    }).catch((e) => console.log(e));
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const move = (source, destination, droppableSource, droppableDestination) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);
    destClone.splice(droppableDestination.index, 0, removed);

    const result = {};
    result["source"] = sourceClone;
    result["destination"] = destClone;
    return result;
  };

  const [checklistItemFontSize, setChecklistItemFontSize] = useLocalStorage(
    "checklistItemFontSize",
    1.0
  );

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1,
      },
    },
  };

  const checklistVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1],
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      style={{ width: "100%", marginTop: 16 }}
    >
      {checklists.length === 0 || !checklists ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              backgroundColor:
                theme === "dark"
                  ? "rgba(35, 35, 35, 0.5)"
                  : "rgba(245, 245, 245, 0.8)",
              border:
                theme === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.08)",
              borderRadius: 3,
            }}
          >
            <ChecklistIcon
              sx={{
                fontSize: 48,
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.3)"
                    : "rgba(0, 0, 0, 0.3)",
                mb: 2,
              }}
            />
            <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
              {jobData.studio === "Post Production" ? "No checklist yet" : "No checklists yet"}
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                borderRadius: 2,
                px: 3,
                background:
                  theme === "dark"
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                "&:hover": {
                  background:
                    theme === "dark"
                      ? "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)"
                      : "linear-gradient(135deg, #3d8bfe 0%, #00d4fe 100%)",
                },
              }}
              onClick={() => {
                if (!jobData?.id) return;
                
                // Create different default checklists based on job type
                let defaultChecklists;
                if (jobData.studio === "Post Production") {
                  // Post Production gets a single "Checklist"
                  defaultChecklists = [
                    {
                      name: "Checklist",
                      id: uuidv4(),
                      checklistItems: [],
                    },
                  ];
                } else {
                  // Default checklists for other job types
                  defaultChecklists = [
                    {
                      name: "Colour Corrections",
                      id: uuidv4(),
                      checklistItems: [],
                    },
                    {
                      name: "Colour Changes",
                      id: uuidv4(),
                      checklistItems: [],
                    },
                    {
                      name: "Retouching Notes",
                      id: uuidv4(),
                      checklistItems: [],
                    },
                  ];
                }
                
                updateDoc(doc(db, "JobData", jobData.id), {
                  checklists: defaultChecklists,
                });
              }}
            >
              {jobData.studio === "Post Production" ? "Create Checklist" : "Create Checklists"}
            </Button>
          </Paper>
        </motion.div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Box sx={{ width: "100%" }}>
            <AnimatePresence>
              {checklists &&
                checklists.map((checklist, index) => (
                  <motion.div
                    key={checklist.id}
                    variants={checklistVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    style={{ marginBottom: 24 }}
                  >
                    <ChecklistV2
                      jobData={jobData}
                      checklist={checklist}
                      checklists={checklists}
                      setChecklists={setChecklists}
                      checklistItemFontSize={checklistItemFontSize}
                      setChecklistItemFontSize={setChecklistItemFontSize}
                      setUrlModalUrl={setUrlModalUrl}
                      setURLModalOpen={setURLModalOpen}
                      idList={idList}
                    />
                  </motion.div>
                ))}
            </AnimatePresence>
          </Box>
        </DragDropContext>
      )}
    </motion.div>
  );
};

export default ChecklistsV2;

const ChecklistV2 = ({
  jobData,
  checklist,
  checklists,
  setChecklists,
  checklistItemFontSize,
  setChecklistItemFontSize,
  setUrlModalUrl,
  setURLModalOpen,
  idList,
}) => {
  const [theme] = useAtom(themeAtom);
  const [showImages, setShowImages] = useState(false);
  const [percentage, setPercentage] = useState(0);
  const [isExploding, setIsExploding] = useState(false);
  const [hideComplete, setHideComplete] = useState(false);

  useEffect(() => {
    if (checklist?.checklistItems?.length > 0) {
      let partial = 0;
      checklist?.checklistItems?.forEach((ci) => {
        if (ci.checked) partial = partial + 1;
      });
      const p = calculatePercentage(partial, checklist.checklistItems.length);
      setPercentage(p);
    } else {
      setPercentage(0);
    }
    // Ensure hideComplete defaults to false unless explicitly set to true
    setHideComplete(checklist.hideComplete === true);
  }, [checklist]);

  function calculatePercentage(partialValue, totalValue) {
    return (100 * partialValue) / totalValue;
  }

  const AddNewListItemTextID = `${checklist.id}_AddNewCheckListItem_Title`;

  const [addNewItemActive, setAddNewItemActive] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const handleNewItemTitleChange = (e) => {
    setNewTitle(fixMarkdown(e.target.value));
  };
  const handleAddNewItemClick = () => {
    setAddNewItemActive(true);
  };
  const addChecklistItem = async () => {
    if (!checklist.checklistItems) checklist.checklistItems = [];
    checklist.checklistItems.push({
      id: uuidv4(),
      title: newTitle,
      checked: false,
    });
    setChecklists([...checklists]);
    setAddNewItemActive(false);

    await updateDoc(doc(db, "JobData", jobData.id), {
      checklists: checklists,
    }).catch((e) => console.log(e));
    setNewTitle("");
    handleAddNewItemClick();
  };
  const handleCancelClick = () => {
    setNewTitle("");
    setAddNewItemActive(false);
  };

  const [hiddenMenuAnchorEle, setHiddenMenuAnchorEle] = useState(null);
  const handleHiddenMenuClick = (event) => {
    setHiddenMenuAnchorEle(event.currentTarget);
  };
  const handleHiddenMenuClose = () => {
    setHiddenMenuAnchorEle(null);
  };

  const [contextMenu, setContextMenu] = useState(null);
  const handleContextMenu = (event) => {
    event.preventDefault();
    if (contextMenu) {
      setContextMenu(null);
    } else {
      setContextMenu({
        mouseX: event.clientX + 2,
        mouseY: event.clientY - 6,
      });
    }
  };
  const handleContextClose = () => {
    setContextMenu(null);
  };

  const [groupWhenFiltering, setGroupWhenFiltering] = useAtom(
    groupWhenFilteringAtom
  );
  const [filterWhenCompleting, setFilterWhenCompleting] = useAtom(
    filterWhenCompletingAtom
  );

  const [currentStaffData] = useAtom(currentStaffDataAtom);

  return (
    <div
      style={{
        display:
          idList === "5f081763cd97556d03075c86" &&
          checklist.name.includes("Checklist")
            ? "none"
            : "block",
      }}
    >
      <Menu
        anchorEl={hiddenMenuAnchorEle}
        open={Boolean(hiddenMenuAnchorEle)}
        onClose={handleHiddenMenuClose}
        PaperProps={{
          style: {
            background: theme === "dark" ? "#2a2a2a" : "#f5f5f5",
            borderRadius: 12,
            border:
              theme === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <Box sx={{ width: 200, p: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, color: "text.secondary" }}
          >
            Font Size
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={checklistItemFontSize}
            disabled
            label="Current Size"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                try {
                  setChecklistItemFontSize((prev) => {
                    const newNum = parseFloat(prev) + 0.1;
                    return parseFloat(newNum).toFixed(1);
                  });
                } catch (e) {
                  console.log("Error adjusting font size");
                }
              }}
            >
              <AddIcon />
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                try {
                  setChecklistItemFontSize((prev) => {
                    const newNum = parseFloat(prev) - 0.1;
                    return parseFloat(newNum).toFixed(1);
                  });
                } catch (e) {
                  console.log("Error adjusting font size");
                }
              }}
            >
              <RemoveIcon />
            </Button>
          </Box>
        </Box>
      </Menu>

      <Menu
        open={Boolean(contextMenu)}
        onClose={handleContextClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          style: {
            background: theme === "dark" ? "#2a2a2a" : "#f5f5f5",
            borderRadius: 12,
            border:
              theme === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setFilterWhenCompleting(!filterWhenCompleting);
            updateDoc(doc(db, "StaffData", currentStaffData.id), {
              filterWhenCompleting: !filterWhenCompleting,
            });
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {filterWhenCompleting ? (
              <CheckBoxIcon />
            ) : (
              <CheckBoxOutlineBlankIcon />
            )}
            Filter When Completing
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setGroupWhenFiltering(!groupWhenFiltering);
            updateDoc(doc(db, "StaffData", currentStaffData.id), {
              groupWhenFiltering: !groupWhenFiltering,
            });
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {groupWhenFiltering ? (
              <CheckBoxIcon />
            ) : (
              <CheckBoxOutlineBlankIcon />
            )}
            Group When Filtering
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setHideComplete(!hideComplete);
            checklist.hideComplete = !hideComplete;
            updateDoc(doc(db, "JobData", jobData.id), {
              checklists: checklists,
            }).catch((e) => console.log(e));
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {hideComplete ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
            Hide Complete
          </Box>
        </MenuItem>
      </Menu>

      <Card
        elevation={0}
        sx={{
          background:
            theme === "dark"
              ? "linear-gradient(135deg, rgba(35, 35, 35, 0.8) 0%, rgba(45, 45, 45, 0.6) 100%)"
              : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(250, 250, 250, 0.8) 100%)",
          backdropFilter: "blur(0.001px)",
          border:
            theme === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.08)",
          borderRadius: 3,
          overflow: "visible",
        }}
      >
        <CardContent sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={handleHiddenMenuClick}
            >
              <ChecklistIcon
                sx={{
                  color: theme === "dark" ? "#90caf9" : "#1976d2",
                  mr: 1.5,
                  fontSize: 24,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: theme === "dark" ? "#ffffff" : "#1f1f1f",
                }}
              >
                {checklist.name}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Images
                </Typography>
                <Switch
                  checked={showImages}
                  onChange={(e) => setShowImages(e.target.checked)}
                  size="small"
                />
              </Box>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme === "dark" ? "#90caf9" : "#1976d2",
                  minWidth: 30,
                  textAlign: "center",
                }}
              >
                {checklist?.checklistItems?.length || 0}
              </Typography>

              <IconButton
                size="small"
                onClick={() => {
                  filterArray(
                    checklist,
                    checklists,
                    groupWhenFiltering,
                    jobData
                  );
                }}
                onContextMenu={handleContextMenu}
                sx={{
                  color: theme === "dark" ? "#90caf9" : "#1976d2",
                  "&:hover": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(144, 202, 249, 0.1)"
                        : "rgba(25, 118, 210, 0.1)",
                  },
                }}
              >
                <Badge color="primary" variant="dot" invisible={!hideComplete}>
                  <FilterListIcon />
                </Badge>
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme === "dark" ? "#90caf9" : "#1976d2",
                  fontWeight: 600,
                }}
              >
                {Math.trunc(percentage)}% Complete
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.08)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  background:
                    theme === "dark"
                      ? "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)",
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {isExploding && (
              <ConfettiExplosion
                particleCount={140}
                particleSize={15}
                height={"120vh"}
                duration={2200}
                zIndex={9999}
              />
            )}
          </Box>

          <List sx={{ width: "100%", p: 0 }}>
            <Droppable droppableId={checklist.id}>
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ minHeight: "40px" }}
                >
                  {checklist.checklistItems &&
                    checklist.checklistItems.map((ci, index) =>
                      !hideComplete || !ci.checked ? (
                        <ChecklistItemV2
                          key={ci.id || `item-${index}`}
                          jobData={jobData}
                          checklist={checklist}
                          checklists={checklists}
                          setChecklists={setChecklists}
                          index={index}
                          ci={ci}
                          setIsExploding={setIsExploding}
                          checklistItemFontSize={checklistItemFontSize}
                          setUrlModalUrl={setUrlModalUrl}
                          setURLModalOpen={setURLModalOpen}
                          showImages={showImages}
                        />
                      ) : null
                    )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </List>

          {addNewItemActive ? (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  id={AddNewListItemTextID}
                  value={newTitle}
                  onChange={handleNewItemTitleChange}
                  placeholder="Add an item..."
                  autoFocus
                  onKeyPress={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      addChecklistItem();
                      return false;
                    }
                  }}
                  onBlur={() => {
                    if (newTitle !== "") {
                      addChecklistItem();
                    } else {
                      setAddNewItemActive(false);
                    }
                    return false;
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                    },
                  }}
                />
                <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={addChecklistItem}
                    sx={{ borderRadius: 2 }}
                  >
                    Add
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCancelClick}
                    sx={{ borderRadius: 2 }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Button
                size="small"
                variant="contained"
                onClick={handleAddNewItemClick}
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  background:
                    theme === "dark"
                      ? "rgba(144, 202, 249, 0.1)"
                      : "rgba(25, 118, 210, 0.1)",
                  color: theme === "dark" ? "#90caf9" : "#1976d2",
                  "&:hover": {
                    background:
                      theme === "dark"
                        ? "rgba(144, 202, 249, 0.2)"
                        : "rgba(25, 118, 210, 0.2)",
                  },
                }}
              >
                <AddIcon sx={{ mr: 1, fontSize: 16 }} />
                Add an item
              </Button>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const filterArray = (checklist, checklists, groupWhenFiltering, jobData) => {
  let sortedArray = [];
  if (groupWhenFiltering) {
    const checklistItems = checklist.checklistItems;
    const checkedRaw = checklistItems.filter((i) => i.checked);
    const uncheckedRaw = checklistItems.filter((i) => !i.checked);
    const checked = alphanumericalSort(checkedRaw, "title");
    const unchecked = alphanumericalSort(uncheckedRaw, "title");
    sortedArray = [...checked, ...unchecked];
  } else {
    sortedArray = alphanumericalSort(checklist.checklistItems, "title");
  }
  checklist.checklistItems = sortedArray;
  updateDoc(doc(db, "JobData", jobData.id), {
    checklists: checklists,
  }).catch((e) => console.log(e));
};

const ChecklistItemV2 = ({
  jobData,
  checklist,
  checklists,
  setChecklists,
  index,
  ci,
  setIsExploding,
  checklistItemFontSize,
  setUrlModalUrl,
  setURLModalOpen,
  showImages,
}) => {
  const [theme] = useAtom(themeAtom);
  const [filterWhenCompleting] = useAtom(filterWhenCompletingAtom);
  const [groupWhenFiltering] = useAtom(groupWhenFilteringAtom);

  const [menuAnchorEle, setMenuAnchorEle] = useState(null);
  const handleClick = (event) => {
    setMenuAnchorEle(event.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuAnchorEle(null);
  };

  const ListItemTextID = `${ci.title}_${index}_CheckListItem_Title`;
  const CheckboxID = `${ci.title}_${index}_CheckListItem_Checkbox`;
  const TextAreaID = `${ci.title}_${index}_CheckListItem_TextArea`;

  const [title, setTitle] = useState("");
  const [checked, setChecked] = useState(ci.checked);
  const [editMode, setEditMode] = useState(false);

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };

  const toggleCheck = (e) => {
    if (editMode) return;
    const newChecked = !checked;
    setChecked(newChecked);
    ci.checked = newChecked;

    let totalChecked = 0;
    checklist.checklistItems.forEach((c) => {
      if (c.checked) totalChecked = totalChecked + 1;
    });
    if (totalChecked === checklist.checklistItems.length) {
      setIsExploding(true);
      setTimeout(() => {
        setIsExploding(false);
      }, 3000);
    }
    if (ci.outsourcer) ci.outsourcer = false;
    updateDoc(doc(db, "JobData", jobData.id), {
      checklists: checklists,
    }).catch((e) => console.log(e));

    if (filterWhenCompleting) {
      filterArray(checklist, checklists, groupWhenFiltering, jobData);
    }
  };

  const handleClose = () => {
    setTitle(ci.title.replace(/^\s+|\s+$/g, ""));
    setEditMode(false);
  };

  const handleSave = () => {
    setEditMode(false);
    ci.title = fixMarkdown(title);
    updateDoc(doc(db, "JobData", jobData.id), {
      checklists: checklists,
    }).catch((e) => console.log(e));
  };

  const handleItemDelete = () => {
    handleMenuClose();
    checklist.checklistItems.splice(index, 1);
    setChecklists([...checklists]);
    updateDoc(doc(db, "JobData", jobData.id), {
      checklists: checklists,
    }).catch((e) => console.log(e));
  };

  useEffect(() => {
    if (ci) {
      setTitle(ci.title ? ci.title.replace(/^\s+|\s+$/g, "") : "");
      setChecked(!!ci.checked);
    }
  }, [ci]);

  const [showOptions] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const fileUploadInput = useRef(null);

  const handleAttachImageClick = () => {
    handleMenuClose();
    fileUploadInput.current.click();
  };

  const handleAttachment = async (e) => {
    setIsUploading(true);
    const files = fileUploadInput.current.files;

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Generate unique filename similar to screenshot approach
        const random = uuidv4().slice(0, 5);
        const ext = file.name.match(/(\.[^.]+)$/)?.[1] || "";
        const fnOrig = file.name.replace(ext, "");
        const fileName = `${fnOrig}_${random}${ext}`;

        // File size check (150MB limit like original)
        if (file.size > 150000000) {
          alert("File must be under 150MB");
          continue;
        }

        try {
          // Upload to Firebase Storage using same pattern as screenshots
          // Path: ChecklistImages/{jobID}/{fileName}
          const storageRef = ref(
            storage,
            `ChecklistImages/${jobData.id}/${fileName}`
          );

          // Upload file to Firebase Storage
          await uploadBytes(storageRef, file);

          // Get download URL
          const url = await getDownloadURL(storageRef);

          if (url) {
            console.log("File uploaded successfully:", url);
            ci.image = url;

            // Update Firestore with new image URL
            await updateDoc(doc(db, "JobData", jobData.id), {
              checklists: checklists,
            });
          }
        } catch (uploadError) {
          console.error("Error uploading file:", uploadError);
          alert("Failed to upload file. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error in handleAttachment:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteImageClick = async () => {
    handleMenuClose();
    ci.image = "";
    await updateDoc(doc(db, "JobData", jobData.id), {
      checklists: checklists,
    }).catch((e) => {
      setIsUploading(false);
      console.log(e);
    });
  };

  const handleExcludeNoteToggle = async () => {
    if (ci.excluded) {
      ci.excluded = false;
    } else {
      ci.excluded = true;
    }

    await updateDoc(doc(db, "JobData", jobData.id), {
      checklists: checklists,
    }).catch((e) => {
      setIsUploading(false);
      console.log(e);
    });
  };

  const handleDuplicateNote = () => {
    handleMenuClose();
    const duplicatedItem = {
      ...ci,
      id: uuidv4(),
      checked: false, // Reset checked state for the duplicate
    };
    checklist.checklistItems.splice(index + 1, 0, duplicatedItem);
    setChecklists([...checklists]);
    updateDoc(doc(db, "JobData", jobData.id), {
      checklists: checklists,
    }).catch((e) => console.log(e));
  };


  return (
    <Draggable key={ci.id} draggableId={ci.id} index={index}>
      {(provided, snapshot) => {
        if (snapshot.isDragging) {
          provided.draggableProps.style = {
            ...provided.draggableProps.style,
            left: provided.draggableProps.style.offsetLeft,
            top: provided.draggableProps.style.offsetTop,
          };
        }
        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            data-color-mode={theme === "dark" ? "dark" : "light"}
            style={{
              ...provided.draggableProps.style,
            }}
          >
            <ListItem
              disablePadding
              sx={{
                borderRadius: 2,
                mb: 1,
                background: snapshot.isDragging
                  ? theme === "dark"
                    ? "rgba(144, 202, 249, 0.1)"
                    : "rgba(25, 118, 210, 0.1)"
                  : "transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    theme === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                },
              }}
            >
              <Menu
                anchorEl={menuAnchorEle}
                open={Boolean(menuAnchorEle)}
                onClose={handleMenuClose}
                PaperProps={{
                  style: {
                    background: theme === "dark" ? "#2a2a2a" : "#f5f5f5",
                    borderRadius: 12,
                    border:
                      theme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                <MenuItem onClick={handleExcludeNoteToggle}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {!ci.excluded ? (
                      <>
                        <CheckBoxOutlineBlankIcon />
                        Exclude Note
                      </>
                    ) : (
                      <>
                        <CheckBoxIcon />
                        Include Note
                      </>
                    )}
                  </Box>
                </MenuItem>
                <MenuItem onClick={handleDuplicateNote}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ContentCopyIcon />
                    Duplicate Note
                  </Box>
                </MenuItem>
                <MenuItem onClick={handleAttachImageClick}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ImageIcon />
                    Attach Image
                  </Box>
                </MenuItem>
                {ci.image ? (
                  <MenuItem onClick={handleDeleteImageClick}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <DeleteIcon />
                      Delete Image
                    </Box>
                  </MenuItem>
                ) : null}
                <MenuItem onClick={handleItemDelete}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DeleteIcon />
                    Delete
                  </Box>
                </MenuItem>
              </Menu>

              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  alignItems: editMode ? "flex-start" : "center",
                  p: 1,
                }}
              >
                <ListItemIcon sx={{ minWidth: 42 }}>
                  <Checkbox
                    edge="start"
                    checked={checked}
                    onClick={toggleCheck}
                    disableRipple
                    id={CheckboxID}
                    size="small"
                    sx={{
                      color: theme === "dark" ? "#90caf9" : "#1976d2",
                      "&.Mui-checked": {
                        color: theme === "dark" ? "#90caf9" : "#1976d2",
                      },
                    }}
                  />
                </ListItemIcon>

                {editMode ? (
                  <Box sx={{ flexGrow: 1 }}>
                    <TextField
                      id={TextAreaID}
                      value={title}
                      onChange={handleTitleChange}
                      multiline
                      rows={3}
                      fullWidth
                      autoFocus
                      onKeyPress={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          handleSave();
                          return false;
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(0, 0, 0, 0.02)",
                        },
                      }}
                    />
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        gap: 1,
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSave}
                          sx={{ borderRadius: 2 }}
                        >
                          Save
                        </Button>
                        <IconButton size="small" onClick={handleClose}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <IconButton size="small" onClick={handleClick}>
                        <MoreHorizIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Box
                      onClick={() => setEditMode(true)}
                      id={ListItemTextID}
                      sx={{
                        flexGrow: 1,
                        cursor: "pointer",
                        p: 1,
                        borderRadius: 1,
                        "&:hover": {
                          background:
                            theme === "dark"
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(0, 0, 0, 0.02)",
                        },
                      }}
                    >
                      {ci.outsourcer ? (
                        <Box
                          sx={{
                            background:
                              theme === "dark"
                                ? "rgba(59, 130, 246, 0.12)"
                                : "rgba(59, 130, 246, 0.08)",
                            borderRadius: 2,
                            p: 0.75,
                            border:
                              theme === "dark"
                                ? "1.5px solid rgba(59, 130, 246, 0.4)"
                                : "1.5px solid rgba(59, 130, 246, 0.3)",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                theme === "dark"
                                  ? "rgba(96, 165, 250, 0.9)"
                                  : "rgba(37, 99, 235, 0.85)",
                              fontWeight: 600,
                              display: "block",
                              mb: 0.25,
                              fontSize: "0.7rem",
                            }}
                          >
                            Outsourcer
                          </Typography>
                          <MDEditor.Markdown
                            source={`${
                              ci.checked && !ci.outsourcer ? "~~" : ""
                            }${title}${
                              ci.checked && !ci.outsourcer ? "~~" : ""
                            }`}
                            style={{
                              background: "transparent",
                              fontSize: `${checklistItemFontSize}em`,
                              color: ci.checked ? "#808080" : "",
                            }}
                          />
                        </Box>
                      ) : ci.excluded ? (
                        <Box
                          sx={{
                            background:
                              theme === "dark"
                                ? "rgba(239, 68, 68, 0.12)"
                                : "rgba(239, 68, 68, 0.08)",
                            borderRadius: 2,
                            p: 0.75,
                            border:
                              theme === "dark"
                                ? "1.5px solid rgba(239, 68, 68, 0.4)"
                                : "1.5px solid rgba(239, 68, 68, 0.3)",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                theme === "dark"
                                  ? "rgba(248, 113, 113, 0.9)"
                                  : "rgba(220, 38, 38, 0.85)",
                              fontWeight: 600,
                              display: "block",
                              mb: 0.25,
                              fontSize: "0.7rem",
                            }}
                          >
                            Excluded
                          </Typography>
                          <MDEditor.Markdown
                            source={`${ci.checked ? "~~" : ""}${title}${
                              ci.checked ? "~~" : ""
                            }`}
                            style={{
                              background: "transparent",
                              fontSize: `${checklistItemFontSize}em`,
                              color: ci.checked ? "#808080" : "",
                            }}
                          />
                        </Box>
                      ) : (
                        <MDEditor.Markdown
                          source={`${ci.checked ? "~~" : ""}${title}${
                            ci.checked ? "~~" : ""
                          }`}
                          style={{
                            background: "transparent",
                            fontSize: `${checklistItemFontSize}em`,
                            color: ci.checked ? "#808080" : "",
                          }}
                        />
                      )}
                    </Box>

                    {isUploading && (
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                    )}

                    {ci.image && !isUploading && (
                      <PopupState variant="popover" popupId="image-popover">
                        {(popupState) => (
                          <>
                            {showImages ? (
                              <Box
                                {...bindHover(popupState)}
                                component="img"
                                src={ci.image}
                                alt={ci.title}
                                sx={{
                                  maxHeight: 60,
                                  maxWidth: 60,
                                  mr: 1,
                                  cursor: "pointer",
                                  borderRadius: 1,
                                  objectFit: "cover",
                                }}
                                onClick={() => {
                                  setUrlModalUrl(ci.image);
                                  setURLModalOpen(true);
                                }}
                              />
                            ) : (
                              <IconButton
                                {...bindHover(popupState)}
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={() => {
                                  setUrlModalUrl(ci.image);
                                  setURLModalOpen(true);
                                }}
                              >
                                <ImageIcon fontSize="small" />
                              </IconButton>
                            )}
                            <HoverPopover
                              {...bindPopover(popupState)}
                              slotProps={{
                                paper: { style: { padding: 10 } },
                              }}
                              anchorOrigin={{
                                vertical: "center",
                                horizontal: "left",
                              }}
                              transformOrigin={{
                                vertical: "center",
                                horizontal: "right",
                              }}
                            >
                              <Box
                                component="img"
                                src={ci.image}
                                alt={ci.title}
                                sx={{
                                  width: "300px",
                                  height: "300px",
                                  objectFit: "contain",
                                }}
                              />
                            </HoverPopover>
                          </>
                        )}
                      </PopupState>
                    )}

                    {!editMode && showOptions && (
                      <IconButton
                        size="small"
                        onClick={handleClick}
                        sx={{ ml: 1 }}
                      >
                        <MoreHorizIcon fontSize="small" />
                      </IconButton>
                    )}
                  </>
                )}

                <input
                  type="file"
                  ref={fileUploadInput}
                  multiple={true}
                  style={{ display: "none" }}
                  onChange={handleAttachment}
                />
              </Box>
            </ListItem>
          </div>
        );
      }}
    </Draggable>
  );
};

function fixMarkdown(val) {
  val = val.replace("* ", "*");
  val = val.replace(" *", "*");
  val = val.replace(" `", "`");
  val = val.replace("` ", "`");
  val = val.replace(" _", "_");
  val = val.replace("_ ", "_");
  return val;
}

function alphanumericalSort(array, key) {
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });

  // Create a shallow copy of the array
  const newArray = [...array];

  return newArray.sort((a, b) => collator.compare(a[key], b[key]));
}
